import { useLazyQuery, useMutation } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { Button, Card, Theme, makeStyles } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import {
    AutocompleteChangeReason,
    AutocompleteInputChangeReason,
} from '@material-ui/lab'
import { FirmAutoSuggestQuery } from 'api/graphQL/queries/StudioManagement/studioManagement.queries'
import { ProductSearchAutosuggestResult } from 'api/graphQL/types/product.type'
import clsx from 'clsx'
import React, { ChangeEvent } from 'react'
import { AutoCompleteInput } from './AutoCompleteInput'

import { ProjectManufacturerProductDetail } from '@deltek/specification-client/core/Models/projectManufacturerProductDetail'
import { v4 as newGuid } from 'uuid'

const ADD_BUTTON_ACTIVE_BG = '#1899d4'
const ADD_BUTTON_HOVER_BG = '#1371a8'
const ADD_BUTTON_INACTIVE_BG = '#8BCCE9'
const MAX_MANUFACTURER_NAME_LENGTH = 200
/** Minimum search text required by the backend to return results */
const DEFAULT_SEARCH_TEXT = 'a'

const useStyles = makeStyles((theme: Theme) => ({
    cardContainer: {
        maxWidth: '90%',
        padding: theme.spacing(1, 2),
    },
    inputWrapper: {
        paddingBottom: theme.spacing(0.5),
    },
    actions: {
        paddingTop: theme.spacing(1),
    },
    addManufacturerButtonActive: {
        backgroundColor: ADD_BUTTON_ACTIVE_BG,
        color: theme.palette.common.white,
        fontWeight: 400,
        cursor: 'pointer',
        transition: 'background-color 0.3s ease',
        '&:hover': {
            backgroundColor: ADD_BUTTON_HOVER_BG,
            boxShadow: '0 0 5px rgba(0, 0, 0, 0.3)',
        },
    },
    addManufacturerButtonInactive: {
        backgroundColor: ADD_BUTTON_INACTIVE_BG,
        color: theme.palette.common.white,
        fontWeight: 400,
        pointerEvents: 'none',
    },
}))

export interface AddManufacturerPopupProps {
    closePopup: () => void
    elementId: string
    productFamilyId: string
    projectId: string
    localProductListings: Partial<ProjectManufacturerProductDetail>[]
    onManufacturerAdded?: (manufacturerName: string) => void
}

export interface FirmAutoSuggestResult {
    firmAutoSuggest: Partial<ProductSearchAutosuggestResult>
}

export const parseFirmName = (firmName: string | undefined): string => {
    if (!firmName) return ''

    if (firmName.includes('%|%')) {
        return firmName.split('%|%')[0]?.slice(1, -1)
    }
    return firmName
}

export const extractListingText = (text: string | undefined): string => {
    return (text ?? '').replace(/^\[/, '').replace(/\]$/, '')
}

export const filterAutoSuggestFirms = (
    firmAutosuggestData: Partial<FirmAutoSuggestResult>,
    productListings: Partial<ProjectManufacturerProductDetail>[]
): string[] => {
    const validFirmNames: string[] =
        firmAutosuggestData?.firmAutoSuggest?.result
            ?.map((name: string) => parseFirmName(name))
            .filter((name: string) => !!name) ?? []

    const productListingFirms = productListings.map((x) =>
        extractListingText(x.listingText)
    )

    const notAddedFirmNames = validFirmNames.filter(
        (name: string) =>
            !productListingFirms.some((productFirm) =>
                productFirm?.toLowerCase().includes(name.toLowerCase())
            )
    )

    const uniqueFirmNames = [...new Set(notAddedFirmNames)]

    return uniqueFirmNames
}

interface FirmDisplayName {
    getFirmByDisplayName: { firmId: string; displayName: string }
}

const AddManufacturerPopup: React.FC<AddManufacturerPopupProps> = ({
    closePopup,
    elementId,
    productFamilyId,
    projectId,
    localProductListings,
    onManufacturerAdded,
}) => {
    const classes = useStyles()

    const [autoSuggestResults, setAutoSuggestResults] = React.useState<
        Record<string, string[]>
    >({})

    const [selectedValue, setSelectedValue] = React.useState<string>()
    const [firmName, setFirmName] = React.useState<string>('')
    const [firmDetails, setFirmDetails] = React.useState<
        FirmDisplayName | undefined
    >()

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [firmAutoSuggest, { loading: firmAutoLoading }] = useLazyQuery<{
        firmAutoSuggest: Partial<ProductSearchAutosuggestResult>
    }>(FirmAutoSuggestQuery, {
        fetchPolicy: 'no-cache',
        onCompleted: React.useCallback(
            (firmAutosuggestData: Partial<FirmAutoSuggestResult>) => {
                const autoSuggestList = filterAutoSuggestFirms(
                    firmAutosuggestData,
                    localProductListings
                )
                setAutoSuggestResults({ firmName: autoSuggestList })
            },
            [localProductListings]
        ),
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'Something went wrong with autosuggest',
                    variant: 'error',
                    id: 'autosuggest_Error',
                },
            })
        },
    })

    const callAutoSuggestQuery = React.useCallback(
        (searchField: string, value: string) => {
            firmAutoSuggest({
                variables: {
                    input: {
                        firmAccountTypes: 'BuildingProductManufacturer',
                        searchLimit: 50,
                        searchField: 'DisplayNameWithFirmName',
                        searchText: value.trim() || DEFAULT_SEARCH_TEXT,
                    },
                },
            })
        },
        [firmAutoSuggest]
    )

    const handleOpen = React.useCallback(
        (searchField = '') => callAutoSuggestQuery(searchField, ''),
        [callAutoSuggestQuery]
    )

    const isAddButtonActive = !!firmDetails || !!firmName

    const handleInputChange = React.useCallback(
        (
            event: ChangeEvent<{}>,
            value: string,
            reason: AutocompleteInputChangeReason
        ) => {
            if (reason !== 'clear') {
                const targetInput = event?.currentTarget as HTMLInputElement
                const searchField = targetInput?.name
                if (targetInput?.name === 'firmName') {
                    setFirmName(targetInput?.value)
                }
                callAutoSuggestQuery(searchField, value)
            }
        },
        [callAutoSuggestQuery]
    )

    const handleChange = React.useCallback(
        (
            e: ChangeEvent<{}>,
            value: string | null,
            reason: AutocompleteChangeReason
        ) => {
            if (reason === 'clear') {
                setFirmDetails(undefined)
            } else {
                const manufacturerName = parseFirmName(value ?? '')

                const manufacturerExists = localProductListings.some(
                    (x) =>
                        extractListingText(x.listingText) === manufacturerName
                )

                if (manufacturerExists) {
                    showGlobalSnackbar({
                        variables: {
                            message: 'Error: Manufacturer already exists',
                            variant: 'error',
                            id: 'AddManufacturerPopup_SaveManufacturer_Error',
                        },
                    })
                    return
                }

                setSelectedValue(manufacturerName)
                setFirmDetails({
                    getFirmByDisplayName: {
                        displayName: manufacturerName,
                        firmId: newGuid(),
                    },
                })

                onManufacturerAdded?.(manufacturerName)
            }
        },
        [onManufacturerAdded, localProductListings, showGlobalSnackbar]
    )

    const handleSave = React.useCallback((): void => {
        const trimmed = firmName.trim()
        if (!trimmed) return

        if (trimmed.length > MAX_MANUFACTURER_NAME_LENGTH) {
            showGlobalSnackbar({
                variables: {
                    message: `Manufacturer name must be ${MAX_MANUFACTURER_NAME_LENGTH} characters or fewer`,
                    variant: 'error',
                    id: 'AddManufacturerPopup_ValidationError',
                },
            })
            return
        }

        handleChange({} as ChangeEvent<{}>, trimmed, 'create-option')
    }, [firmName, handleChange, showGlobalSnackbar])

    return (
        <Card
            data-testid="add-manufacturer-popup"
            variant="elevation"
            square
            elevation={2}
            className={classes.cardContainer}
        >
            <div className={classes.inputWrapper}>
                <AutoCompleteInput
                    name="firmName"
                    label="Manufacturer Name"
                    handleChange={handleChange}
                    handleInputChange={handleInputChange}
                    handleOpen={(_e: ChangeEvent<{}>) =>
                        handleOpen?.('firmName')
                    }
                    hasError={false}
                    selectedValue={selectedValue}
                    loading={firmAutoLoading}
                    items={autoSuggestResults.firmName ?? []}
                    formState={{}}
                    listBoxProps={{
                        style: {
                            maxHeight: '225px',
                        },
                    }}
                />
            </div>

            <div className={classes.actions}>
                <Button
                    data-testid="add-new-manufacturer"
                    onClick={handleSave}
                    variant="contained"
                    disabled={!isAddButtonActive}
                    className={clsx({
                        [classes.addManufacturerButtonActive]: isAddButtonActive,
                        [classes.addManufacturerButtonInactive]: !isAddButtonActive,
                    })}
                    startIcon={<AddIcon />}
                >
                    {'Add New Manufacturer'}
                </Button>
            </div>
        </Card>
    )
}

export default AddManufacturerPopup
